import React, { useContext, useState, useEffect } from 'react';

import styled from 'styled-components';

import { getCustomFieldValue } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadEvent, useRemoveEvent, useAddModelToDate } from '@hooks/API/Event/UseEvent';
import { forceReloadProject } from '@hooks/API/Project/UseProject';
import useDepartmentName from '@hooks/CachedLists/UseDepartmentName';

import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import { useBookingEvent } from '@components/EventDrawer/BookingEvent/BookingEventContext';
import EventPlanner from '@components/EventDrawer/BookingEvent/BookingEventDetails/EventPlanner';
import EventDatesAndModels from '@components/EventDrawer/BookingEvent/EventDatesAndModels';
import { EventAddModelQuestionBooking } from '@components/EventDrawer/EventDrawerUi/EventAddModelQuestion';
import EventDaysHeader from '@components/EventDrawer/EventDrawerUi/EventDaysHeader';
import {
    EventDetailsWrapper,
    ObjectHistoryContainer,
    BookingEventTitleText,
    ProjectClientWrapper,
} from '@components/EventDrawer/EventDrawerUi/EventUI';
import { Loading } from '@components/Gui/Loading';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import ResponsiveFlexContainer from '@components/Gui/ResponsiveFlexContainer';
import ProjectChangeCustomer from '@components/Project/ProjectEditDrawer/ProjectChangeCustomer';
import ProjectEventsTimeline from '@components/Project/ProjectEventsTimeline/ProjectEventsTimeline';
import SendProjectDrawerFromEvent from '@components/SendProjectAgendaDrawer/SendProjectDrawerFromEvent';

import { editDrawerTypes, extendOptionNumberToAllDates } from './bookingEventDetailsUtils';
import BookingEventDrawers from './BookingEventDrawers';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const EventTitleContainer = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: ${(props) => (props.$isMobile ? '0 1.5rem 0 1.5rem' : '0 2rem 0 2.5rem')};

    .event-title-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-width: 9rem;

        .project-send-button {
            font-size: 1.4rem;
        }

        @media (max-width: 768px) {
            justify-content: flex-end;
            gap: 1rem;
        }
    }

    @media (max-width: 768px) {
        display: block;
    }
`;

const BookingEventDetails = ({
    eventData,
    filterModel,
    dateSelected,
    afterUpdate,
    afterRemove,
    plainMode,
    onSelectEvent,
}) => {
    const { GetEventClientRole, cachedList } = useContext(ListContext);
    const { mutateAsync: onRemoveEvent } = useRemoveEvent();
    const [customFields, setCustomFields] = useState([]);
    const [validCustomFieldsKeys, setValidCustomFieldsKeys] = useState([]);
    const [selectedWorkingDate, setSelectedWorkingDate] = useState();
    const [selectedWorkingDay, setSelectedWorkingDay] = useState();
    const [editDrawerType, setEditDrawerType] = useState();
    const [eventDates, setEventDates] = useState([]);
    const [viewDetails, setViewDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [viewEventPlanner, setViewEventPlanner] = useState(false);
    const { mutateAsync: onAddModelToDate } = useAddModelToDate();
    const [sendProjectDrawer, setSendProjectDrawer] = useState(false);
    const [isChangeCustomerDrawer, setIsChangeCustomerDrawer] = useState(false);

    const { isMobile, breakpoint } = useBookingEvent();

    useEffect(() => {
        setViewDetails(false);
    }, [filterModel]);

    useEffect(() => {
        let sortedEventDates = eventData && eventData.EventDates ? [...eventData.EventDates] : [];
        sortedEventDates.sort((date1, date2) => (date1.FromDate > date2.FromDate ? 1 : -1));
        setEventDates(sortedEventDates);
    }, [eventData]);

    useEffect(() => {
        const customfields = cachedList.customFields.items['Event'];
        setCustomFields(customfields);
        setValidCustomFieldsKeys(customfields.filter((field) => !field.Disabled).map((field) => field.Name));
    }, [cachedList]);

    const DepartmentName = useDepartmentName(eventData.DepartmentCreated);

    const handleShareProject = () => {
        setSendProjectDrawer(true);
    };

    const handleRemoveEvent = async () => {
        try {
            await onRemoveEvent({ ID: eventData.ID });
            afterRemove();
        } catch (error) {
            console.error('Remove Event Error => ', error);
            // setIsFetching(false);
        }
    };

    const onExtendOptionNumberToAllDates = (modelId, optionNumber) => {
        const newEventDates = extendOptionNumberToAllDates(eventDates, modelId, optionNumber);
        setEventDates(newEventDates);
    };

    const onOpenEditWorkingDateDrawer = (workingDateOptions) => {
        setSelectedWorkingDate(workingDateOptions);
        setEditDrawerType(editDrawerTypes.editDates);
    };

    const onOpenEditWorkingDayDrawer = (workingDayOptions) => {
        setSelectedWorkingDay(workingDayOptions);
        setEditDrawerType(editDrawerTypes.editWorkingDay);
    };

    const onCloseDrawers = () => {
        setEditDrawerType(undefined);
        setSelectedWorkingDate(undefined);
    };

    const getEventClientRoleLabel = (roleID) => {
        const role = GetEventClientRole(roleID);
        if (role) {
            return role;
        } else {
            return 'Event Client';
        }
    };

    const getEventTitle = () => {
        if (eventData.EventClientID && eventData.Name === getContactName(eventData.EventClientID)) {
            return (
                <BookingEventTitleText className={eventData.Cancel ? 'is-cancelled' : null}>
                    <strong>{getContactName(eventData.EventClientID)}</strong> <em>/</em>{' '}
                    {getEventClientRoleLabel(eventData.EventClientRoleID)}
                </BookingEventTitleText>
            );
        }
        return <BookingEventTitleText>{eventData.Name}</BookingEventTitleText>;
    };

    const isActiveBookingEvent = () => {
        let eventDateModels = [];
        if (filterModel) {
            if (eventData && eventData.EventDates) {
                for (const eventDate of eventData.EventDates) {
                    eventDateModels = [...eventDateModels, ...eventDate.EventDatesModels];
                }
                if (eventDateModels.find((model) => model.ModelID === Number(filterModel))) {
                    return true;
                }
            }
            return false;
        }
        return true;
    };

    const handleAddModelToEventDate = async (eventDateId) => {
        setLoading(true);
        if (eventDateId) {
            await onAddModelToDate({ modelId: filterModel, eventDateId: eventDateId });
        } else {
            for (const eventDate of eventData.EventDates) {
                await onAddModelToDate({ modelId: filterModel, eventDateId: eventDate.ID });
            }
        }
        forceReloadEvent(eventData.ID);
        setLoading(false);
        if (eventData.Project) {
            forceReloadProject(eventData.Project.ID);
        }
        setViewDetails(true);
    };

    const handleViewEventPlanner = () => {
        setViewEventPlanner(true);
    };

    const detailsLeftMinWidth = 180;

    const renderEventFields = (eventData) => {
        return (
            <EventDetailsWrapper className={eventData.Cancel ? 'is-cancelled' : null}>
                <ResponsiveFlexContainer
                    leftMinWidth={detailsLeftMinWidth}
                    columns={[
                        {
                            content: <p className="is-label">Department</p>,
                            isBorder: true,
                        },
                        {
                            content: <p className="is-content">{DepartmentName}</p>,
                            isBorder: false,
                        },
                    ]}
                />
                {/* {(eventData.Name && eventData.Name !== getContactName(eventData.EventClientID)) && */}
                <ResponsiveFlexContainer
                    leftMinWidth={detailsLeftMinWidth}
                    columns={[
                        {
                            content: <p className="is-label">{getEventClientRoleLabel(eventData.EventClientRoleID)}</p>,
                            isBorder: true,
                        },
                        {
                            content: <p className="is-content">{getContactName(eventData.EventClientID)}</p>,
                            isBorder: false,
                        },
                    ]}
                />
                {/* } */}
                <ResponsiveFlexContainer
                    leftMinWidth={detailsLeftMinWidth}
                    columns={[
                        {
                            content: <p className="is-label">Description</p>,
                            isBorder: true,
                        },
                        {
                            content: <p className="is-content">{eventData.Description}</p>,
                            isBorder: false,
                        },
                    ]}
                />
                <ResponsiveFlexContainer
                    leftMinWidth={detailsLeftMinWidth}
                    columns={[
                        {
                            content: <p className="is-label">Internal Notes</p>,
                            isBorder: true,
                        },
                        {
                            content: <p className="is-content">{eventData.InternalNote}</p>,
                            isBorder: false,
                        },
                    ]}
                />
                {Object.keys(eventData.CustomFields).map((key) => {
                    if (validCustomFieldsKeys && validCustomFieldsKeys.includes(key)) {
                        return (
                            <ResponsiveFlexContainer
                                leftMinWidth={detailsLeftMinWidth}
                                key={key}
                                columns={[
                                    {
                                        content: <p className="is-label">{key.replace(/_/g, ' ')}</p>,
                                        isBorder: true,
                                    },
                                    {
                                        content: (
                                            <p className="is-content">
                                                {getCustomFieldValue(key, eventData.CustomFields[key], customFields)}
                                            </p>
                                        ),
                                        isBorder: false,
                                    },
                                ]}
                            />
                        );
                    }
                })}
            </EventDetailsWrapper>
        );
    };

    return loading ? (
        <Loading textBlack />
    ) : (
        <div>
            {editDrawerType && (
                <BookingEventDrawers
                    eventData={eventData}
                    selectedWorkingDate={selectedWorkingDate}
                    afterUpdate={afterUpdate}
                    editDrawerType={editDrawerType}
                    onCloseDrawers={onCloseDrawers}
                    customFields={customFields}
                    selectedWorkingDay={selectedWorkingDay}
                />
            )}
            {!filterModel || isActiveBookingEvent() || viewDetails ? (
                <>
                    {!plainMode && !isMobile && (
                        <ProjectEventsTimeline
                            event={eventData}
                            projectID={eventData.Project.ID}
                            onSelectEvent={onSelectEvent}
                            showPackage
                        />
                    )}
                    <ProjectClientWrapper $isMobile={isMobile}>
                        <ResponsiveFlexContainer
                            leftMinWidth={detailsLeftMinWidth}
                            columns={[
                                {
                                    content: <p className="is-label">Project Client</p>,
                                    isBorder: true,
                                },
                                {
                                    content: (
                                        <p className="is-content">
                                            {eventData.Project.Contact.Name}
                                            <NwLinkButton
                                                label="change"
                                                style={{ marginLeft: '0.5rem' }}
                                                onClick={() => setIsChangeCustomerDrawer(true)}
                                            />
                                        </p>
                                    ),
                                    isBorder: false,
                                },
                            ]}
                        />
                    </ProjectClientWrapper>
                    <EventTitleContainer $isMobile={isMobile}>
                        {getEventTitle(eventData)}
                        <div className="event-title-actions">
                            <div className="project-send-button">
                                <NwIcon
                                    key="share"
                                    icon={byPrefixAndName.fasl['share']}
                                    tooltip="Send schedule by e-mail"
                                    pointer
                                    onClick={handleShareProject}
                                />
                            </div>
                            <NwDropDownMenu
                                right
                                actions={[
                                    {
                                        name: '',
                                        key: 'edit',
                                        tooltip: 'edit general details',
                                        pinned: true,
                                        icon: byPrefixAndName.fasl['pen'],
                                        action: () => setEditDrawerType(editDrawerTypes.editGeneralDetails),
                                    },
                                    {
                                        name: 'edit custom fields',
                                        key: 'custom',
                                        tooltip: 'edit custom fields',
                                        action: () => setEditDrawerType(editDrawerTypes.editCustomFields),
                                    },
                                    {
                                        name: 'delete',
                                        type: 'delete',
                                        key: 'delete',
                                        tooltip: 'delete event',
                                        icon: byPrefixAndName.fasl['trash-alt'],
                                        confirmationPlacement: 'left',
                                        action: () => handleRemoveEvent(),
                                    },
                                ]}
                            />
                        </div>
                    </EventTitleContainer>
                    {renderEventFields(eventData)}
                    <ObjectHistoryContainer>
                        <ObjectHistory
                            object={eventData}
                            onIconClick={() => setEditDrawerType(editDrawerTypes.history)}
                            asLine
                        />
                    </ObjectHistoryContainer>
                    <EventDaysHeader
                        eventData={eventData}
                        plainMode={plainMode}
                        onAddDays={() => setEditDrawerType(editDrawerTypes.addDates)}
                        //onAddModels={handleQuickAdd}
                        onEventPlanner={handleViewEventPlanner}
                        afterUpdate={() => {
                            forceReloadEvent(eventData.ID);
                            afterUpdate();
                        }}
                    />
                    {eventDates.length > 0 && (
                        <>
                            {eventDates.map((eventDate) => {
                                const isActive =
                                    !filterModel ||
                                    eventDate.EventDatesModels.find((model) => model.ModelID === filterModel)
                                        ? true
                                        : false;
                                return (
                                    <div key={eventDate.ID}>
                                        <EventDatesAndModels
                                            filterModel={filterModel}
                                            eventData={eventData}
                                            selectedModel={isActive ? filterModel : null}
                                            dateSelected={dateSelected}
                                            loadEventData={() => {
                                                forceReloadEvent(eventData.ID);
                                            }}
                                            onExtendOptionNumberToAllDates={onExtendOptionNumberToAllDates}
                                            afterUpdate={afterUpdate}
                                            eventDateInfo={eventDate}
                                            onOpenEditWorkingDateDrawer={onOpenEditWorkingDateDrawer}
                                            onOpenEditWorkingDayDrawer={onOpenEditWorkingDayDrawer}
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </>
            ) : (
                <EventAddModelQuestionBooking
                    eventData={eventData}
                    model={filterModel}
                    onAddModelToEventDate={handleAddModelToEventDate}
                    onShowEvent={() => setViewDetails(true)}
                />
            )}
            {viewEventPlanner && (
                <NwDrawer padding="0px" onClose={() => setViewEventPlanner(false)} title="Event Planner">
                    <EventPlanner eventData={eventData} afterUpdate={afterUpdate} />
                </NwDrawer>
            )}
            {sendProjectDrawer && (
                <SendProjectDrawerFromEvent event={eventData} onClose={() => setSendProjectDrawer(false)} />
            )}
            {isChangeCustomerDrawer && (
                <ProjectChangeCustomer
                    project={eventData.Project}
                    onClose={() => setIsChangeCustomerDrawer(false)}
                    eventID={eventData.ID}
                />
            )}
        </div>
    );
};

export default BookingEventDetails;
